import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Button, Card, CardContent, Chip, Divider, IconButton, Stack, TextField, Tooltip, Typography } from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

function useScriptOnce(src: string) {
  const loadedRef = useRef(false);
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;
    const existing = Array.from(document.getElementsByTagName('script')).find(s => (s.getAttribute('src') || '').includes(src));
    if (existing) return;
    const s = document.createElement('script');
    s.async = true;
    s.src = src;
    document.head.appendChild(s);
    return () => { try { document.head.removeChild(s); } catch {} };
  }, [src]);
}

const CodeBox: React.FC<{ code: string; onCopy?: () => void }>
  = ({ code, onCopy }) => (
  <Box sx={{ position: 'relative', bgcolor: (t)=> t.palette.mode==='dark'?'#0b1020':'#0f172a', color:'#e5e7eb', borderRadius: 1, p: 2, fontFamily:'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}>
    <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{code}</Box>
    <Tooltip title="Copy">
      <IconButton size="small" onClick={onCopy} sx={{ position: 'absolute', top: 6, right: 6, color: '#e5e7eb' }} aria-label="Copy code">
        <ContentCopyIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </Box>
);

const Widgets: React.FC = () => {
  const origin = (typeof window !== 'undefined' && window.location.origin) ? window.location.origin : 'https://www.askthebill.ai';
  // Ensure the widget script is available for live preview
  useScriptOnce(`${origin}/askthebill.widget.js?v=20261005`);

  // Bill selector (simple manual fields for minimal implementation)
  const [congress, setCongress] = useState<string>('118');
  const [billType, setBillType] = useState<string>('hr');
  const [billNumber, setBillNumber] = useState<string>('1');

  const billIdent = useMemo(() => `${congress}/${billType}/${billNumber}`, [congress, billType, billNumber]);

  // Allow advanced users to override apiBase (optional)
  const [apiBase, setApiBase] = useState<string>('');

  const billSnippet = useMemo(() => {
    const base = apiBase && apiBase.trim() ? apiBase.trim() : `${origin}/api`;
    return [
      `<script>window.AskTheBill = { apiBase: '${base}' };</script>`,
      `<script async src="${origin}/askthebill.widget.js?v=20261005"></script>`,
      `<div data-askthebill="bill-status" data-bill="${billIdent}"></div>`,
    ].join('\n');
  }, [apiBase, origin, billIdent]);

  const trendingSnippet = useMemo(() => {
    const base = apiBase && apiBase.trim() ? apiBase.trim() : `${origin}/api`;
    return [
      `<script>window.AskTheBill = { apiBase: '${base}' };</script>`,
      `<script async src="${origin}/askthebill.widget.js?v=20261005"></script>`,
      `<div data-askthebill="trending-bills"></div>`,
    ].join('\n');
  }, [apiBase, origin]);

  // Live preview: call window.AskTheBill.render() when inputs change
  useEffect(() => {
    const t = setTimeout(() => {
      try { (window as any).AskTheBill && typeof (window as any).AskTheBill.render === 'function' && (window as any).AskTheBill.render(); } catch {}
    }, 50);
    return () => clearTimeout(t);
  }, [billIdent]);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      alert('Copied to clipboard');
    } catch {
      // Fallback: no-op
    }
  };

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>Embeddable Widgets</Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
        Add AskTheBill widgets to your website to drive backlinks and share real-time congressional data. Copy the snippet and paste it where you want the widget to appear.
      </Typography>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>1) Configure</Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
            <TextField label="Congress" size="small" value={congress} onChange={(e)=> setCongress(e.target.value)} sx={{ width: 120 }} />
            <TextField label="Type" size="small" value={billType} onChange={(e)=> setBillType(e.target.value)} sx={{ width: 120 }} helperText="e.g., hr, s, hres, sres" />
            <TextField label="Number" size="small" value={billNumber} onChange={(e)=> setBillNumber(e.target.value)} sx={{ width: 140 }} />
            <Chip label={billIdent.toUpperCase()} />
          </Stack>
          <TextField fullWidth label="API Base (optional)" size="small" value={apiBase} onChange={(e)=> setApiBase(e.target.value)} placeholder={`${origin}/api`} helperText="Only override if your API is proxied under a different path or host." />
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Bill Status Widget</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Renders a compact card showing the bill title, identifier, latest action, and a link back to AskTheBill.
          </Typography>
          <Box sx={{ mb: 2 }}>
            <CodeBox code={billSnippet} onCopy={() => copy(billSnippet)} />
          </Box>
          <Typography variant="subtitle2" gutterBottom>Live Preview</Typography>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <div data-askthebill="bill-status" data-bill={billIdent}></div>
          </Box>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>Trending Bills Widget</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Lists currently trending bills (by follows/engagement) with links back to AskTheBill.
          </Typography>
          <Box sx={{ mb: 2 }}>
            <CodeBox code={trendingSnippet} onCopy={() => copy(trendingSnippet)} />
          </Box>
          <Typography variant="subtitle2" gutterBottom>Live Preview</Typography>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 2 }}>
            <div data-askthebill="trending-bills"></div>
          </Box>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>How it works</Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Include the script once per page, then add one or more <code>div</code> containers with <code>data-askthebill</code> attributes.
          </Typography>
          <Stack spacing={1}>
            <Typography variant="body2">• Script URL: <code>{origin}/askthebill.widget.js?v=20261005</code></Typography>
            <Typography variant="body2">• Supported containers: <code>data-askthebill="bill-status"</code> with <code>data-bill="congress/type/number"</code>; <code>data-askthebill="trending-bills"</code></Typography>
            <Typography variant="body2">• Optional: set <code>window.AskTheBill = {'{ apiBase: "{origin}/api" }'}</code> before the script to specify the API base URL.</Typography>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Widgets;
